import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, RotateCcw, PencilLine, Trash2, ChevronRight,
  Bold, Italic, Code, Quote, List, ListOrdered,
  Link, ImagePlus, Heading1, Heading2, Heading3,
  Maximize, Minimize,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { blogSpanComponent } from "../../utils/blogColors";
import {
  getOwner,
  getProjects,
  getAchievements,
  getSkills,
  getHackathons,
  getCertificates,
  getTimeline,
  getBlogPosts,
  upsertOwner,
  createProject,
  updateProject,
  deleteProject,
  createAchievement,
  updateAchievement,
  deleteAchievement,
  createSkill,
  updateSkill,
  deleteSkill,
  createHackathon,
  updateHackathon,
  deleteHackathon,
  createCertificate,
  updateCertificate,
  deleteCertificate,
  createTimeline,
  updateTimeline,
  deleteTimeline,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
} from "../../services/firestore";
import { getResumeDownloadUrl, replaceResumeFile, uploadCertificateLogo, deleteCertificateLogo, uploadBlogImage, deleteBlogImage } from "../../services/storage";
import Toast, { useToast } from "./Toast";
import { useMarkdownToolbar } from "../../hooks/useMarkdownToolbar";
import "./AdminPanel.css";

const SECTION_CONFIG = {
  owner: {
    label: "Owner",
    singular: "owner profile",
    fields: ["name", "title", "email", "github", "linkedin", "bio"],
    required: ["name", "email"],
    columns: ["name", "title", "email", "github", "linkedin"],
    read: () => getOwner(),
    normalize: (data) => (data ? [{ id: "owner", ...data }] : []),
  },
  project: {
    label: "Projects",
    singular: "project",
    fields: ["title", "description", "tech_stack", "category", "github_url", "live_url", "featured"],
    required: ["title", "description"],
    columns: ["title", "category", "tech_stack", "featured", "github_url"],
    read: () => getProjects(),
    normalize: (data) => data || [],
  },
  achievement: {
    label: "Achievements",
    singular: "achievement",
    fields: ["title", "description", "category", "date", "icon"],
    required: ["title"],
    columns: ["title", "category", "date", "icon"],
    read: () => getAchievements(),
    normalize: (data) => data || [],
  },
  skill: {
    label: "Skills",
    singular: "skill",
    fields: ["name", "category", "level", "icon"],
    required: ["name"],
    columns: ["name", "category", "level", "icon"],
    read: () => getSkills(),
    normalize: (data) => {
      if (!data || typeof data !== "object") return [];
      return Object.entries(data).flatMap(([category, list]) =>
        (list || []).map((item) => ({ ...item, category: item.category || category }))
      );
    },
  },
  hackathon: {
    label: "Hackathons",
    singular: "hackathon",
    fields: ["name", "organizer", "result", "project_name", "project_url", "date", "location", "description"],
    required: ["name"],
    columns: ["name", "organizer", "result", "date", "location"],
    read: () => getHackathons(),
    normalize: (data) => data || [],
  },
  certificate: {
    label: "Certificates",
    singular: "certificate",
    fields: ["title", "issuer", "credential_id", "credential_url", "issue_date", "expiry_date", "description"],
    required: ["title", "issuer"],
    columns: ["title", "issuer", "issue_date", "expiry_date"],
    read: () => getCertificates(),
    normalize: (data) => data || [],
  },
  timeline: {
    label: "Timeline",
    singular: "timeline entry",
    fields: ["title", "description", "date", "order"],
    required: ["title"],
    columns: ["title", "date", "order"],
    read: () => getTimeline(),
    normalize: (data) => data || [],
  },
  blog: {
    label: "Blog",
    singular: "blog post",
    fields: ["title", "slug", "excerpt", "date", "tags"],
    required: ["title", "content", "date"],
    columns: ["title", "date", "tags"],
    read: () => getBlogPosts(),
    normalize: (data) => data || [],
  },
};

const SKILL_CATEGORIES = [
  "Languages",
  "Frontend & Backend",
  "Data Science & ML",
  "Databases & Systems",
  "Cloud, DevOps & Tools",
  "Other",
];

const HINTS = {
  tech_stack: "comma-separated: React, Python, Flask",
  level:      "0–100",
  date:       "YYYY-MM-DD or YYYY-MM or YYYY (sorted latest first)",
  issue_date: "YYYY-MM-DD",
  expiry_date: "YYYY-MM-DD",
  icon:       "emoji: 🏆",
  featured:   "show in featured cards",
  order:      "sort order (lower first)",
  slug:       "auto-generated from title; edit to override",
  tags:       "comma-separated: react, javascript, web",
  excerpt:    "short summary for listing cards",
};

const FIELD_LABELS = {
  name: "Name",
  title: "Title",
  email: "Email",
  github: "GitHub",
  linkedin: "LinkedIn",
  bio: "Bio",
  description: "Description",
  tech_stack: "Tech Stack",
  category: "Category",
  github_url: "GitHub URL",
  live_url: "Live URL",
  featured: "Featured",
  date: "Date",
  icon: "Icon",
  organizer: "Organizer",
  result: "Result",
  project_name: "Project Name",
  project_url: "Project URL",
  location: "Location",
  issuer: "Issuer",
  credential_id: "Credential ID",
  credential_url: "Credential URL",
  issue_date: "Issue Date",
  expiry_date: "Expiry Date",
  level: "Level",
  order: "Order",
  slug: "Slug",
  tags: "Tags",
  excerpt: "Excerpt",
};

const DEFAULT_SECTION = "project";

function getEmptyRowsBySection() {
  return Object.keys(SECTION_CONFIG).reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});
}

function formatCell(value, field) {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const text = String(value);
  if (["description", "bio"].includes(field) && text.length > 80) {
    return `${text.slice(0, 80)}...`;
  }
  return text;
}

function parseValueForField(field, value) {
  if (field === "tech_stack") {
    return (value || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  }

  if (field === "level") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : 50;
  }

  if (field === "order") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (field === "featured") {
    return Boolean(value);
  }

  if (["date", "issue_date", "expiry_date"].includes(field)) {
    return value || null;
  }

  return value;
}

function toFormData(section, row) {
  const formData = {};
  for (const field of section.fields) {
    if (field === "tech_stack") {
      const stack = row[field];
      formData[field] = Array.isArray(stack) ? stack.join(", ") : stack || "";
      continue;
    }
    if (field === "tags") {
      const tags = row[field];
      formData[field] = Array.isArray(tags) ? tags.join(", ") : tags || "";
      continue;
    }
    if (field === "featured") {
      formData[field] = Boolean(row[field]);
      continue;
    }
    formData[field] = row[field] ?? "";
  }
  return formData;
}

function buildResumeHistoryEntry(owner) {
  if (!owner?.resume_path && !owner?.resume_storage_url && !owner?.resume_name) {
    return null;
  }

  return {
    resume_path: owner.resume_path || "",
    resume_storage_url: owner.resume_storage_url || "",
    resume_name: owner.resume_name || "resume.pdf",
    resume_updated_at: owner.resume_updated_at || "",
  };
}

function buildPayload(section, formData) {
  const payload = {};

  for (const field of section.fields) {
    const raw = formData[field];
    if (section.required.includes(field) && (raw === undefined || raw === null || raw === "")) {
      throw new Error(`Missing required field: ${FIELD_LABELS[field] || field}`);
    }

    if (!section.required.includes(field) && (raw === undefined || raw === "")) {
      continue;
    }

    payload[field] = parseValueForField(field, raw);
  }

  return payload;
}

function ActionIcon({ name }) {
  switch (name) {
    case "add":
      return <Plus aria-hidden="true" focusable="false" />;
    case "refresh":
      return <RotateCcw aria-hidden="true" focusable="false" />;
    case "edit":
      return <PencilLine aria-hidden="true" focusable="false" />;
    case "delete":
      return <Trash2 aria-hidden="true" focusable="false" />;
    default:
      return null;
  }
}

export default function AdminPanel({ auth = null, email = "", pageMode = false, onLogout = null }) {
  const [activeSection, setActiveSection] = useState(DEFAULT_SECTION);
  const [rowsBySection, setRowsBySection] = useState(getEmptyRowsBySection());
  const [loadingBySection, setLoadingBySection] = useState({});
  const [tableError, setTableError] = useState("");
  const [formMode, setFormMode] = useState("add");
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [actionBusy, setActionBusy] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { toasts, addToast, removeToast } = useToast();
  const [resumeFile, setResumeFile] = useState(null);
  const [showResumeHistory, setShowResumeHistory] = useState(false);
  const [openingResumePath, setOpeningResumePath] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [blogImageFile, setBlogImageFile] = useState(null);
  const [removeBlogImage, setRemoveBlogImage] = useState(false);
  const [blogContent, setBlogContent] = useState("");
  const [blogFullscreen, setBlogFullscreen] = useState(false);
  const [imagePromptOpen, setImagePromptOpen] = useState(false);
  const [imagePromptAlt, setImagePromptAlt] = useState("");
  const [imagePromptUrl, setImagePromptUrl] = useState("");

  const section = SECTION_CONFIG[activeSection];
  const rows = rowsBySection[activeSection] || [];
  const loading = Boolean(loadingBySection[activeSection]);
  const currentOwner = rows[0] || {};

  const currentLogoUrl =
    activeSection === "certificate" && formMode === "edit"
      ? rows.find((r) => r.id === editingId)?.image_url || ""
      : "";

  const currentBlogCoverUrl =
    activeSection === "blog" && formMode === "edit"
      ? rows.find((r) => r.id === editingId)?.cover_image_url || ""
      : "";

  const sectionKeys = useMemo(() => Object.keys(SECTION_CONFIG), []);

  const { textareaRef, bold, italic, code, quote, ul, ol, link, h1, h2, h3, insertImage } =
    useMarkdownToolbar(setBlogContent);

  const refreshSection = async (key) => {
    const cfg = SECTION_CONFIG[key];
    setLoadingBySection((prev) => ({ ...prev, [key]: true }));
    setTableError("");

    try {
      const raw = await cfg.read();
      const normalized = cfg.normalize(raw);
      setRowsBySection((prev) => ({ ...prev, [key]: normalized }));
    } catch (e) {
      setTableError(e.message || "Failed to load data");
    } finally {
      setLoadingBySection((prev) => ({ ...prev, [key]: false }));
    }
  };

  const resetFormForAdd = () => {
    setFormMode("add");
    setEditingId(null);
    setFormData({});
    setEditModalOpen(false);
    setResumeFile(null);
    setShowResumeHistory(false);
    setOpeningResumePath("");
    setLogoFile(null);
    setRemoveLogo(false);
    setBlogImageFile(null);
    setRemoveBlogImage(false);
    setBlogContent("");
    setBlogFullscreen(false);
    setImagePromptOpen(false);
  };

  useEffect(() => {
    refreshSection(activeSection);
    resetFormForAdd();
  }, [activeSection]);

  const beginEdit = (row) => {
    setFormMode("edit");
    setEditingId(row.id);
    setFormData(toFormData(section, row));
    setEditModalOpen(true);
    setResumeFile(null);
    setShowResumeHistory(false);
    setOpeningResumePath("");
    setLogoFile(null);
    setRemoveLogo(false);
    setBlogImageFile(null);
    setRemoveBlogImage(false);
    setBlogContent(row.content || "");
    setBlogFullscreen(false);
    setImagePromptOpen(false);
  };

  const beginCreate = () => {
    setFormMode("add");
    setEditingId(null);
    setFormData({});
    setEditModalOpen(true);
    setLogoFile(null);
    setRemoveLogo(false);
    setBlogImageFile(null);
    setRemoveBlogImage(false);
    setBlogContent("");
    setBlogFullscreen(false);
    setImagePromptOpen(false);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setFormMode("add");
    setEditingId(null);
    setFormData({});
    setResumeFile(null);
    setShowResumeHistory(false);
    setOpeningResumePath("");
    setLogoFile(null);
    setRemoveLogo(false);
    setBlogImageFile(null);
    setRemoveBlogImage(false);
    setBlogContent("");
  };

  const openArchivedResume = async (resumePath, resumeStorageUrl = "") => {
    try {
      const openingKey = resumePath || resumeStorageUrl || "";
      setOpeningResumePath(openingKey);

      const url = resumePath ? await getResumeDownloadUrl(resumePath) : resumeStorageUrl;
      if (!url) {
        throw new Error("Unable to open archived resume.");
      }

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      addToast("error", error.message || "Unable to open archived resume.");
    } finally {
      setOpeningResumePath("");
    }
  };

  const handleDelete = async (row) => {
    if (!auth?.currentUser) {
      addToast("error", "Not authenticated. Please sign in again.");
      return;
    }

    const confirmed = window.confirm(`Delete this ${section.singular}? This action cannot be undone.`);
    if (!confirmed) return;

    const deleteMap = {
      project: deleteProject,
      achievement: deleteAchievement,
      skill: deleteSkill,
      hackathon: deleteHackathon,
      certificate: deleteCertificate,
      timeline: deleteTimeline,
      blog: deleteBlogPost,
    };

    const deleteFn = deleteMap[activeSection];
    if (!deleteFn) {
      addToast("error", "Delete is not available for this section.");
      return;
    }

    setActionBusy(true);
    try {
      await deleteFn(row.id);
      setRowsBySection((prev) => ({
        ...prev,
        [activeSection]: prev[activeSection].filter((r) => r.id !== row.id),
      }));
      if (editingId === row.id) resetFormForAdd();
      addToast("success", `${section.singular} deleted.`);
    } catch (e) {
      addToast("error", e.message || "Delete failed");
      await refreshSection(activeSection);
    } finally {
      setActionBusy(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!auth?.currentUser) {
      addToast("error", "Not authenticated. Please sign in again.");
      return;
    }

    setActionBusy(true);

    const createMap = {
      owner: upsertOwner,
      project: createProject,
      achievement: createAchievement,
      skill: createSkill,
      hackathon: createHackathon,
      certificate: createCertificate,
      timeline: createTimeline,
      blog: createBlogPost,
    };

    const updateMap = {
      owner: upsertOwner,
      project: updateProject,
      achievement: updateAchievement,
      skill: updateSkill,
      hackathon: updateHackathon,
      certificate: updateCertificate,
      timeline: updateTimeline,
      blog: updateBlogPost,
    };

    try {
      const payload = buildPayload(section, formData);

      if (activeSection === "owner") {
        payload.resume_url = currentOwner.resume_storage_url || currentOwner.resume_url || "";
        payload.resume_storage_url = currentOwner.resume_storage_url || currentOwner.resume_url || "";
        payload.resume_path = currentOwner.resume_path || "";
        payload.resume_name = currentOwner.resume_name || "";
        payload.resume_updated_at = currentOwner.resume_updated_at || "";
        payload.resume_history = Array.isArray(currentOwner.resume_history) ? [...currentOwner.resume_history] : [];

        if (resumeFile) {
          if (resumeFile.type !== "application/pdf") {
            throw new Error("Resume must be uploaded as a PDF file.");
          }

          const resumeMeta = await replaceResumeFile(resumeFile, currentOwner.resume_path || "");
          const previousVersion = buildResumeHistoryEntry(currentOwner);
          const nextHistory = payload.resume_history;
          if (previousVersion) {
            previousVersion.resume_path = resumeMeta.archived_resume_path || previousVersion.resume_path;
            nextHistory.unshift(previousVersion);
          }
          Object.assign(payload, resumeMeta);
        }
      }

      if (activeSection === "certificate") {
        const previousLogoPath =
          formMode === "edit"
            ? rows.find((r) => r.id === editingId)?.image_path || ""
            : "";

        if (logoFile) {
          const logoMeta = await uploadCertificateLogo(logoFile);
          payload.image_url = logoMeta.url;
          payload.image_path = logoMeta.path;
          if (previousLogoPath) deleteCertificateLogo(previousLogoPath).catch(() => {});
        } else if (removeLogo) {
          payload.image_url = "";
          payload.image_path = "";
          if (previousLogoPath) deleteCertificateLogo(previousLogoPath).catch(() => {});
        }
      }

      if (activeSection === "blog") {
        if (!blogContent && formMode === "add") {
          throw new Error("Missing required field: Content");
        }
        payload.content = blogContent;

        if (formData.tags) {
          payload.tags = formData.tags.split(",").map((t) => t.trim()).filter(Boolean);
        } else {
          payload.tags = [];
        }

        if (!formData.slug && formData.title) {
          payload.slug = formData.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        }

        const previousBlogImagePath =
          formMode === "edit"
            ? rows.find((r) => r.id === editingId)?.cover_image_path || ""
            : "";

        if (blogImageFile) {
          const imgMeta = await uploadBlogImage(blogImageFile);
          payload.cover_image_url = imgMeta.url;
          payload.cover_image_path = imgMeta.path;
          if (previousBlogImagePath) deleteBlogImage(previousBlogImagePath).catch(() => {});
        } else if (removeBlogImage) {
          payload.cover_image_url = "";
          payload.cover_image_path = "";
          if (previousBlogImagePath) deleteBlogImage(previousBlogImagePath).catch(() => {});
        }
      }

      if (formMode === "edit") {
        const updateFn = updateMap[activeSection];
        let result;
        if (activeSection === "owner") {
          result = await updateFn(payload);
          result = { id: "owner", ...result };
        } else {
          result = await updateFn(editingId, payload);
        }
        setRowsBySection((prev) => ({
          ...prev,
          [activeSection]: prev[activeSection].map((r) => (r.id === editingId ? result : r)),
        }));
        addToast("success", `${section.singular} updated.`);
      } else {
        const createFn = createMap[activeSection];
        const result = await createFn(payload);
        setRowsBySection((prev) => ({
          ...prev,
          [activeSection]: [...(prev[activeSection] || []), result],
        }));
        addToast("success", `${section.singular} added.`);
      }

      if (activeSection !== "owner") {
        resetFormForAdd();
      }
      closeEditModal();
    } catch (e) {
      addToast("error", e.message || "Save failed");
      await refreshSection(activeSection);
    } finally {
      setActionBusy(false);
    }
  };

  const tableColumnCount = section.columns.length + 1;

  return (
    <div className={`admin-panel ${pageMode ? "admin-panel--page" : ""}`}>
      <div className="admin-panel__drawer">
        <div className="admin-panel__header">
          <div>
            <h2 className="admin-panel__title">Admin Console</h2>
            <p className="admin-panel__subtitle">Manage all portfolio sections with table-based CRUD.</p>
          </div>
          <div className="admin-panel__header-actions">
            <span className="admin-panel__email">{email || "admin"}</span>
            {pageMode && onLogout && (
              <button className="btn btn-logout" onClick={onLogout} title="Sign out of admin panel">
                sign out
              </button>
            )}
          </div>
        </div>

        <div className="admin-panel__tabs">
          {sectionKeys.map((key) => (
            <button
              key={key}
              className={`admin-tab ${activeSection === key ? "admin-tab--active" : ""}`}
              onClick={() => setActiveSection(key)}
            >
              {SECTION_CONFIG[key].label}
              <span className="admin-tab__count">{rowsBySection[key]?.length || 0}</span>
            </button>
          ))}
        </div>

        <div className="admin-panel__content">
          <section className="admin-table-wrap card">
            <div className="admin-table-wrap__header">
              <h3>{section.label}</h3>
              <div className="admin-table-wrap__actions">
                {activeSection !== "owner" && (
                  <button
                    className="btn btn-outline admin-icon-btn admin-icon-btn--primary"
                    type="button"
                    onClick={beginCreate}
                    title="Add new"
                    aria-label="Add new record"
                  >
                    <ActionIcon name="add" />
                    <span className="admin-add-label">add</span>
                  </button>
                )}
                <button
                  className="btn btn-outline admin-icon-btn admin-icon-btn--neutral"
                  type="button"
                  onClick={() => refreshSection(activeSection)}
                  title="Refresh"
                  aria-label="Refresh records"
                >
                  <ActionIcon name="refresh" />
                </button>
              </div>
            </div>

            {tableError && <div className="admin-status admin-status--err">{tableError}</div>}

            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    {section.columns.map((column) => (
                      <th key={column}>{FIELD_LABELS[column] || column}</th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={tableColumnCount} className="admin-table__empty">Loading data...</td>
                    </tr>
                  )}

                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={tableColumnCount} className="admin-table__empty">
                        No {section.singular} entries found.
                      </td>
                    </tr>
                  )}

                  {!loading && rows.map((row) => (
                    <tr key={row.id || `${activeSection}-${JSON.stringify(row)}`}>
                      {section.columns.map((column) => (
                        <td key={column}>{formatCell(row[column], column)}</td>
                      ))}
                      <td>
                        <div className="admin-row-actions">
                          <button
                            className="btn btn-outline admin-icon-btn admin-icon-btn--info"
                            type="button"
                            onClick={() => beginEdit(row)}
                            disabled={actionBusy}
                            title="Edit"
                            aria-label={`Edit ${section.singular}`}
                          >
                            <ActionIcon name="edit" />
                          </button>
                          {activeSection !== "owner" && (
                            <button
                              className="btn btn-outline admin-icon-btn admin-icon-btn--danger"
                              type="button"
                              onClick={() => handleDelete(row)}
                              disabled={actionBusy}
                              title="Delete"
                              aria-label={`Delete ${section.singular}`}
                            >
                              <ActionIcon name="delete" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="admin-mobile-list">
              {loading && <div className="admin-mobile-list__state">Loading data...</div>}

              {!loading && rows.length === 0 && (
                <div className="admin-mobile-list__state">
                  No {section.singular} entries found.
                </div>
              )}

              {!loading && rows.map((row) => {
                const titleValue = formatCell(row[section.columns[0]], section.columns[0]);
                const secondaryFields = section.columns.slice(1)
                  .map((column) => ({ column, value: formatCell(row[column], column) }))
                  .filter(({ value }) => value !== "-" && value !== titleValue);

                return (
                  <article
                    key={row.id || `${activeSection}-${JSON.stringify(row)}`}
                    className="admin-mobile-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => beginEdit(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        beginEdit(row);
                      }
                    }}
                    aria-label={`Edit ${section.singular}: ${titleValue}`}
                  >
                    <div className="admin-mobile-card__header">
                      <h4 className="admin-mobile-card__title">{titleValue}</h4>
                      <ChevronRight className="admin-mobile-card__chevron" size={18} aria-hidden="true" />
                    </div>

                    {secondaryFields.length > 0 && (
                      <div className="admin-mobile-card__summary">
                        {secondaryFields.slice(0, 3).map(({ column, value }) => (
                          <span key={column} className="admin-mobile-card__meta">{value}</span>
                        ))}
                      </div>
                    )}

                    <div className="admin-mobile-card__footer">
                      <div className="admin-mobile-card__actions">
                        <button
                          className="btn btn-outline admin-icon-btn admin-icon-btn--info"
                          type="button"
                          onClick={(event) => { event.stopPropagation(); beginEdit(row); }}
                          disabled={actionBusy}
                          title="Edit"
                          aria-label={`Edit ${section.singular}`}
                        >
                          <ActionIcon name="edit" />
                        </button>
                        {activeSection !== "owner" && (
                          <button
                            className="btn btn-outline admin-icon-btn admin-icon-btn--danger"
                            type="button"
                            onClick={(event) => { event.stopPropagation(); handleDelete(row); }}
                            disabled={actionBusy}
                            title="Delete"
                            aria-label={`Delete ${section.singular}`}
                          >
                            <ActionIcon name="delete" />
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>

        {editModalOpen && (
          <div className="admin-modal" role="presentation" onClick={closeEditModal}>
            <div
              className={`admin-modal__card card ${activeSection === "blog" ? "admin-modal__card--blog" : ""}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="admin-record-modal-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="admin-modal__header">
                <div>
                  <p className="admin-modal__eyebrow">{formMode === "edit" ? "Edit record" : "Create record"}</p>
                  <h3 id="admin-record-modal-title" className="admin-modal__title">
                    {formMode === "edit" ? `Edit ${section.singular}` : `Add ${section.singular}`}
                  </h3>
                </div>
                <button className="admin-modal__close" type="button" onClick={closeEditModal}>
                  ✕
                </button>
              </div>

              <form className="admin-modal__form admin-panel__form" onSubmit={handleSubmit}>
                {activeSection === "owner" && (
                  <div className="admin-resume card">
                    <div className="admin-resume__header">
                      <div>
                        <h4 className="admin-resume__title">Resume PDF</h4>
                        <p className="admin-resume__subtitle">
                          Upload a new version to replace the current file in Firebase Storage.
                        </p>
                      </div>
                      {(currentOwner.resume_storage_url || currentOwner.resume_url) && (
                        <a
                          href={currentOwner.resume_storage_url || currentOwner.resume_url}
                          target="_blank"
                          rel="noreferrer"
                          className="admin-resume__link"
                        >
                          open current resume
                        </a>
                      )}
                    </div>

                    <div className="admin-resume__meta">
                      <span>
                        Current file: {currentOwner.resume_name || "none uploaded"}
                      </span>
                      {currentOwner.resume_updated_at && (
                        <span>Updated: {new Date(currentOwner.resume_updated_at).toLocaleString()}</span>
                      )}
                    </div>

                    <label className="admin-resume__picker">
                      <span>Replace with PDF</span>
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                      />
                    </label>

                    {resumeFile && (
                      <p className="admin-resume__selected">Selected: {resumeFile.name}</p>
                    )}

                    <div className="admin-resume__history-bar">
                      <button
                        type="button"
                        className="btn btn-outline admin-resume__history-toggle"
                        onClick={() => setShowResumeHistory((value) => !value)}
                      >
                        {showResumeHistory ? "hide_history()" : "view_history()"}
                      </button>
                      <span className="admin-resume__history-count">
                        {Array.isArray(currentOwner.resume_history) ? currentOwner.resume_history.length : 0} previous version(s)
                      </span>
                    </div>

                    {showResumeHistory && (
                      <div className="admin-resume__history-list">
                        {(currentOwner.resume_history || []).length === 0 ? (
                          <p className="admin-resume__history-empty">No previous resume versions yet.</p>
                        ) : (
                          (currentOwner.resume_history || []).map((entry, index) => (
                            <div key={`${entry.resume_path || entry.resume_storage_url || index}`} className="admin-resume__history-item">
                              <div>
                                <p className="admin-resume__history-name">{entry.resume_name || `resume-${index + 1}.pdf`}</p>
                                <p className="admin-resume__history-meta">
                                  {entry.resume_updated_at ? new Date(entry.resume_updated_at).toLocaleString() : "Unknown date"}
                                </p>
                              </div>
                              <button
                                type="button"
                                className="admin-resume__history-link"
                                onClick={() => openArchivedResume(entry.resume_path, entry.resume_storage_url || "")}
                                disabled={openingResumePath === (entry.resume_path || entry.resume_storage_url)}
                              >
                                {openingResumePath === (entry.resume_path || entry.resume_storage_url) ? "opening..." : "open"}
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeSection === "certificate" && (
                  <div className="admin-resume card">
                    <div className="admin-resume__header">
                      <div>
                        <h4 className="admin-resume__title">Issuer logo</h4>
                        <p className="admin-resume__subtitle">
                          Shown next to the certificate on the site instead of the letter badge. Square PNG/WebP/SVG looks best (max 2 MB).
                        </p>
                      </div>
                      {currentLogoUrl && !removeLogo && (
                        <img
                          className="admin-logo-preview"
                          src={currentLogoUrl}
                          alt="current certificate logo"
                        />
                      )}
                    </div>

                    {currentLogoUrl && (
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={removeLogo}
                          onChange={(e) => setRemoveLogo(e.target.checked)}
                        />
                        <span>Remove current logo</span>
                      </label>
                    )}

                    {!removeLogo && (
                      <label className="admin-resume__picker">
                        <span>{currentLogoUrl ? "Replace with new image" : "Upload image"}</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                          onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                        />
                      </label>
                    )}

                    {logoFile && (
                      <p className="admin-resume__selected">Selected: {logoFile.name}</p>
                    )}
                  </div>
                )}

                {activeSection === "blog" && (
                  <div className="admin-resume card">
                    <div className="admin-resume__header">
                      <div>
                        <h4 className="admin-resume__title">Cover image</h4>
                        <p className="admin-resume__subtitle">
                          Displayed at the top of the blog post and in listing cards. Landscape PNG/WebP/JPG works best (max 5 MB).
                        </p>
                      </div>
                      {currentBlogCoverUrl && !removeBlogImage && (
                        <img
                          className="admin-logo-preview"
                          src={currentBlogCoverUrl}
                          alt="current cover"
                        />
                      )}
                    </div>

                    {currentBlogCoverUrl && (
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={removeBlogImage}
                          onChange={(e) => setRemoveBlogImage(e.target.checked)}
                        />
                        <span>Remove current cover</span>
                      </label>
                    )}

                    {!removeBlogImage && (
                      <label className="admin-resume__picker">
                        <span>{currentBlogCoverUrl ? "Replace with new image" : "Upload cover image"}</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          onChange={(e) => setBlogImageFile(e.target.files?.[0] || null)}
                        />
                      </label>
                    )}

                    {blogImageFile && (
                      <p className="admin-resume__selected">Selected: {blogImageFile.name}</p>
                    )}
                  </div>
                )}

                {section.fields.map((field) => (
                  <div key={field} className="admin-field">
                    <label className="admin-field__label">
                      {FIELD_LABELS[field] || field}
                      {section.required.includes(field) && <span className="admin-field__req"> *</span>}
                      {HINTS[field] && <span className="admin-field__hint"> - {HINTS[field]}</span>}
                    </label>

                    {field === "featured" ? (
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={Boolean(formData[field])}
                          onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.checked }))}
                        />
                        <span>Show as featured project</span>
                      </label>
                    ) : field === "description" || field === "bio" ? (
                      <textarea
                        className="admin-input admin-textarea"
                        value={formData[field] ?? ""}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                      />
                    ) : activeSection === "skill" && field === "category" ? (
                      <select
                        className="admin-input"
                        value={formData[field] ?? ""}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                      >
                        <option value="">Select category</option>
                        {SKILL_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="admin-input"
                        type={
                          activeSection === "timeline" && field === "date"
                            ? "text"
                            : ["date", "issue_date", "expiry_date"].includes(field)
                              ? "date"
                              : field === "level" || field === "order"
                                ? "number"
                                : field === "email"
                                  ? "email"
                                  : "text"
                        }
                        value={formData[field] ?? ""}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}

                {activeSection === "blog" && (
                  <div className={`admin-field${blogFullscreen ? " admin-field--fullscreen" : ""}`}>
                    <label className="admin-field__label">
                      Content <span className="admin-field__req">*</span>
                      <span className="admin-field__hint"> - markdown supported</span>
                    </label>
                    <div className={`blog-editor${blogFullscreen ? " blog-editor--fullscreen" : ""}`}>
                      <div className="blog-toolbar">
                        <button type="button" className="blog-toolbar__btn" onClick={bold} title="Bold"><Bold size={15} /></button>
                        <button type="button" className="blog-toolbar__btn" onClick={italic} title="Italic"><Italic size={15} /></button>
                        <button type="button" className="blog-toolbar__btn" onClick={code} title="Inline Code"><Code size={15} /></button>
                        <button type="button" className="blog-toolbar__btn" onClick={quote} title="Blockquote"><Quote size={15} /></button>
                        <span className="blog-toolbar__divider" />
                        <button type="button" className="blog-toolbar__btn" onClick={h1} title="Heading 1"><Heading1 size={15} /></button>
                        <button type="button" className="blog-toolbar__btn" onClick={h2} title="Heading 2"><Heading2 size={15} /></button>
                        <button type="button" className="blog-toolbar__btn" onClick={h3} title="Heading 3"><Heading3 size={15} /></button>
                        <span className="blog-toolbar__divider" />
                        <button type="button" className="blog-toolbar__btn" onClick={ul} title="Bullet List"><List size={15} /></button>
                        <button type="button" className="blog-toolbar__btn" onClick={ol} title="Numbered List"><ListOrdered size={15} /></button>
                        <span className="blog-toolbar__divider" />
                        <button type="button" className="blog-toolbar__btn" onClick={link} title="Insert Link"><Link size={15} /></button>
                        <button type="button" className="blog-toolbar__btn" onClick={() => setImagePromptOpen(true)} title="Insert Image"><ImagePlus size={15} /></button>
                        <span className="blog-toolbar__spacer" />
                        <button type="button" className="blog-toolbar__btn" onClick={() => setBlogFullscreen((f) => !f)} title={blogFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                          {blogFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
                        </button>
                      </div>
                      <div className="blog-editor__panes">
                        <textarea
                          ref={textareaRef}
                          className="admin-input blog-editor__input"
                          value={blogContent}
                          onChange={(e) => setBlogContent(e.target.value)}
                          placeholder="Write your blog post in markdown..."
                        />
                        <div className="blog-editor__preview blog-editor__preview--prose">
                          {blogContent ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={{ span: blogSpanComponent }}>{blogContent}</ReactMarkdown>
                          ) : (
                            <p className="blog-editor__placeholder">Preview will appear here...</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {imagePromptOpen && (
                      <div className="blog-image-prompt" role="dialog" aria-label="Insert image">
                        <div className="blog-image-prompt__row">
                          <label className="blog-image-prompt__label">
                            Alt text
                            <input
                              className="admin-input blog-image-prompt__input"
                              value={imagePromptAlt}
                              onChange={(e) => setImagePromptAlt(e.target.value)}
                              placeholder="Image description"
                              autoFocus
                            />
                          </label>
                          <label className="blog-image-prompt__label">
                            Image URL
                            <input
                              className="admin-input blog-image-prompt__input"
                              value={imagePromptUrl}
                              onChange={(e) => setImagePromptUrl(e.target.value)}
                              placeholder="https://..."
                            />
                          </label>
                        </div>
                        <div className="blog-image-prompt__actions">
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => { setImagePromptOpen(false); setImagePromptAlt(""); setImagePromptUrl(""); }}
                          >
                            cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              if (imagePromptUrl.trim()) {
                                insertImage(imagePromptUrl.trim(), imagePromptAlt.trim());
                                setImagePromptOpen(false);
                                setImagePromptAlt("");
                                setImagePromptUrl("");
                              }
                            }}
                          >
                            insert
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="admin-modal__actions">
                  <button className="btn btn-outline" type="button" onClick={closeEditModal}>
                    cancel
                  </button>
                  <button className="btn btn-primary admin-submit" type="submit" disabled={actionBusy}>
                    {actionBusy ? "Saving..." : formMode === "edit" ? `Update ${section.singular}` : `Add ${section.singular}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

// Exported for testing
export { formatCell, parseValueForField, toFormData, buildResumeHistoryEntry, buildPayload };
